import type { AxiosPromise, AxiosResponse } from 'axios';
import type { ApiErrorDebug, ApiResponse, ErrorResponse } from './responses';
import axios from 'axios';
import { env } from '@/config/env';

/** The bos-api error envelope (`ApiError`) and its older `message` variant. */
type ErrorEnvelope = {
    message?: string;
    error?: string;
    errors?: Record<string, unknown>;
    code?: string;
    dependents?: Record<string, number>;
    correlationId?: string;
    debug?: ApiErrorDebug;
};

/** The optional envelope fields an `ErrorResponse` carries when they are sent. */
const envelopeExtras = (
    data: ErrorEnvelope | undefined,
): Pick<ErrorResponse, 'code' | 'dependents' | 'correlationId' | 'debug'> => ({
    ...(data?.code ? { code: data.code } : {}),
    ...(data?.dependents ? { dependents: data.dependents } : {}),
    ...(data?.correlationId ? { correlationId: data.correlationId } : {}),
    ...(data?.debug ? { debug: data.debug } : {}),
});

/**
 * A development build logs every server exception the API explains, so a 500
 * is visible even on a screen that shows only a short message (BOS-236).
 */
const logServerException = (
    status: number,
    data: ErrorEnvelope | undefined,
) => {
    if (env.isDevelopmentBuild && data?.debug) {
        console.error(
            `bos-api ${status} (${data.debug.mode}, correlation ${data.correlationId ?? 'none'}): ` +
                `${data.debug.type}: ${data.debug.message}\n${data.debug.stackTrace}`,
            data.debug.innerExceptions,
        );
    }
};

export const makeApiRequest = async <T>(
    httpPromise: AxiosPromise,
): Promise<ApiResponse<T>> => {
    let result: ApiResponse<T>;
    let response: AxiosResponse<unknown>;

    try {
        response = await httpPromise;

        if (
            response.status === 200 ||
            response.status === 204 ||
            response.status === 201
        ) {
            const body = response.data as T;
            result = {
                success: true,
                response: body,
            };
        } else {
            // bos-api error envelopes carry the text as `error`; older
            // endpoints use `message`. Accept both.
            const resp = response as AxiosResponse<ErrorEnvelope | undefined>;
            const message = resp.data?.message ?? resp.data?.error ?? '';
            logServerException(response.status, resp.data);
            result = {
                success: false,
                error: {
                    isAxiosError: false,
                    status: response.status,
                    errors: resp?.data?.errors,
                    message: message,
                    name: response.status.toString(),
                    ...envelopeExtras(resp.data),
                },
            };
        }
    } catch (err) {
        const error = err as {
            response?: AxiosResponse<ErrorEnvelope>;
        };

        if (error.response) {
            const resp = error.response;
            const message =
                resp.data && (resp.data.message || resp.data.error)
                    ? (resp.data.message ?? resp.data.error ?? '')
                    : resp.statusText;
            logServerException(resp.status, resp.data);
            result = {
                success: false,
                error: {
                    isAxiosError: false,
                    status: error.response.status,
                    message: message,
                    errors: resp?.data?.errors,
                    name: error.response.status.toString(),
                    ...envelopeExtras(resp.data),
                },
            };
        } else if (axios.isCancel(error)) {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 499,
                    message: 'Request cancelled',
                    name: '499',
                },
            };
        } else {
            result = {
                success: false,
                error: {
                    isAxiosError: true,
                    status: 500,
                    message: 'ERR_CONNECTION_REFUSED. Possible network problem',
                    name: '500',
                },
            };
        }
    }

    return result;
};
